import React, { useState, useEffect } from 'react';
import { Table, Card, Btn, Modal, Input, Select, SearchableSelect, Textarea, FormGrid, SectionHeader, Badge, StatCard, NeedProject, RNLLoader } from '../components/UI';
import { fmt, fmtNum, fmtDate, statusColor, exportCSV } from '../utils';
import * as api from '../api';
import { useParams, useNavigate } from 'react-router-dom';

// ─── PURCHASE REQUESTS ───────────────────────
function PRTab({ projectId, toast }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [detail, setDetail]   = useState(null);
  const [form, setForm]       = useState({ items: [{}] });
  const [saving, setSaving]   = useState(false);

  const load = async () => { setLoading(true); try { const d = await api.listPRs(projectId); setRows(Array.isArray(d)?d:[]); } catch {} setLoading(false); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [projectId]);

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, {}] }));
  const setItem = (i, k, v) => setForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));

  const save = async () => {
    setSaving(true);
    try { await api.createPR({ ...form, project_id: projectId }); toast.show('PR created'); setModal(false); setForm({ items: [{}] }); load(); }
    catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const approve = async (id, status, rejection_reason) => {
    try { await api.updatePRStatus(id, { status, rejection_reason }); toast.show(`PR ${status}`); load(); }
    catch (e) { toast.show(e.message, 'error'); }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 14 }}>
        <Btn variant="ghost" onClick={() => exportCSV(rows, 'purchase-requests')}>⬇ CSV</Btn>
        <Btn onClick={() => { setForm({ items: [{}] }); setModal(true); }}>+ New PR</Btn>
      </div>
      <Card>
        <Table loading={loading} cols={[
          { key: 'pr_number',            label: 'PR #',    render: v => <span style={{ fontFamily:'var(--mono)', color:'var(--accent)' }}>{v}</span> },
          { key: 'title',                label: 'Title' },
          { key: 'status',               label: 'Status',  render: v => <Badge color={statusColor(v)}>{v}</Badge> },
          { key: 'required_date',        label: 'Required',render: v => fmtDate(v) },
          { key: 'total_estimated_cost', label: 'Est. Cost',render: v => <span style={{ fontFamily:'var(--mono)' }}>{fmt(v)}</span> },
          { key: 'items',                label: 'Items',   render: v => v?.length || 0 },
          { key: 'purchase_request_id',  label: '',        render: (id, row) => (
            <div style={{ display:'flex', gap:6 }}>
              <Btn variant="ghost"  size="sm" style={{padding:'3px 8px'}} onClick={async()=>{try{const d=await api.getPR(id);setDetail(d);}catch(e){toast.show(e.message,'error');}}}>View</Btn>
              {row.status==='pending' && <>
                <Btn variant="success" size="sm" style={{padding:'3px 8px'}} onClick={()=>approve(id,'approved')}>✓</Btn>
                <Btn variant="danger"  size="sm" style={{padding:'3px 8px'}} onClick={()=>{const r=window.prompt('Reason for rejection:');if(r)approve(id,'rejected',r);}}>✕</Btn>
              </>}
            </div>
          )},
        ]} rows={rows} />
      </Card>

      {detail && (
        <Modal title={`PR — ${detail.pr_number}`} onClose={() => setDetail(null)} width={700}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight:600, marginBottom:6 }}>{detail.title}</div>
            <div style={{ display:'flex', gap:10, fontSize:12, color:'var(--text-muted)' }}>
              <Badge color={statusColor(detail.status)}>{detail.status}</Badge>
              <span>Required: {fmtDate(detail.required_date)}</span>
              <span>Est: <strong style={{ fontFamily:'var(--mono)', color:'var(--text-primary)' }}>{fmt(detail.total_estimated_cost)}</strong></span>
            </div>
          </div>
          <Table cols={[
            { key:'description',       label:'Description' },
            { key:'quantity',          label:'Qty',    render:v=>fmtNum(v) },
            { key:'unit_of_measure',   label:'Unit' },
            { key:'estimated_unit_rate',label:'Rate',  render:v=>fmt(v) },
            { key:'estimated_amount',  label:'Amount', render:v=><span style={{fontFamily:'var(--mono)',color:'var(--accent)'}}>{fmt(v)}</span> },
          ]} rows={detail.items || []} />
        </Modal>
      )}

      {modal && (
        <Modal title="New Purchase Request" onClose={() => setModal(false)} width={740}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <FormGrid>
              <Input label="PR Number" required value={form.pr_number||''} onChange={v=>setForm(f=>({...f,pr_number:v}))} />
              <Input label="Title" required value={form.title||''} onChange={v=>setForm(f=>({...f,title:v}))} />
              <Input label="Required Date" type="date" value={form.required_date||''} onChange={v=>setForm(f=>({...f,required_date:v}))} />
            </FormGrid>
            <Textarea label="Description" value={form.description||''} onChange={v=>setForm(f=>({...f,description:v}))} rows={2} />
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text-muted)', marginBottom:8 }}>LINE ITEMS</div>
              {form.items.map((item, i) => (
                <div key={i} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', gap:8, marginBottom:8, padding:10, background:'var(--bg-elevated)', borderRadius:8 }}>
                  <Input placeholder="Description" value={item.description||''} onChange={v=>setItem(i,'description',v)} />
                  <Input placeholder="Qty" type="number" value={item.quantity||''} onChange={v=>setItem(i,'quantity',v)} />
                  <Input placeholder="Unit" value={item.unit_of_measure||''} onChange={v=>setItem(i,'unit_of_measure',v)} />
                  <Input placeholder="Est. Rate" type="number" value={item.estimated_unit_rate||''} onChange={v=>setItem(i,'estimated_unit_rate',v)} />
                </div>
              ))}
              <Btn variant="ghost" size="sm" onClick={addItem}>Add Item</Btn>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Submit PR</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── PURCHASE ORDERS ─────────────────────────
function POTab({ projectId, toast }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [detail, setDetail]   = useState(null);
  const [form, setForm]       = useState({ items: [{}] });
  const [saving, setSaving]   = useState(false);

  const load = async () => { setLoading(true); try { const d = await api.listPOs(projectId); setRows(Array.isArray(d)?d:[]); } catch {} setLoading(false); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [projectId]);

  const setItem = (i, k, v) => setForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));

  const save = async () => {
    setSaving(true);
    try { await api.createPO({ ...form, project_id: projectId }); toast.show('PO created'); setModal(false); setForm({ items: [{}] }); load(); }
    catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  return (
    <>
      <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginBottom:14 }}>
        <Btn variant="ghost" onClick={() => exportCSV(rows, 'purchase-orders')}>⬇ CSV</Btn>
        <Btn onClick={() => { setForm({ items:[{}], order_date: new Date().toISOString().slice(0,10) }); setModal(true); }}>+ New PO</Btn>
      </div>
      <Card>
        <Table loading={loading} cols={[
          { key:'po_number',               label:'PO #',     render:v=><span style={{fontFamily:'var(--mono)',color:'var(--accent)'}}>{v}</span> },
          { key:'supplier_name',           label:'Supplier' },
          { key:'status',                  label:'Status',   render:v=><Badge color={statusColor(v)}>{v}</Badge> },
          { key:'order_date',              label:'Date',     render:v=>fmtDate(v) },
          { key:'expected_delivery_date',  label:'Delivery', render:v=>fmtDate(v) },
          { key:'grand_total',             label:'Grand Total',render:v=><span style={{fontFamily:'var(--mono)',fontWeight:700}}>{fmt(v)}</span> },
          { key:'purchase_id',             label:'',         render:(id,row)=>(
            <div style={{display:'flex',gap:6}}>
              <Btn variant="ghost" size="sm" style={{padding:'3px 8px'}} onClick={async()=>{try{const d=await api.getPO(id);setDetail(d);}catch(e){toast.show(e.message,'error');}}}>View</Btn>
              <SearchableSelect value={row.status} onChange={async v=>{try{await api.updatePO(id,{status:v});toast.show('Updated');load();}catch(e2){toast.show(e2.message,'error');}}}
                style={{background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:6,padding:'4px 8px',color:'var(--text-primary)',fontSize:11,fontFamily:'var(--font)'}}
                options={['pending','confirmed','delivered','cancelled'].map(s=>({value:s,label:s}))} />
            </div>
          )},
        ]} rows={rows} />
      </Card>

      {detail && (
        <Modal title={`PO — ${detail.po_number}`} onClose={() => setDetail(null)} width={700}>
          <div className="grid-cols-3" style={{ gap:10, marginBottom:16 }}>
            <StatCard label="Subtotal"    value={fmt(detail.total_amount)} />
            <StatCard label="VAT"         value={fmt(detail.vat_amount)}   color="amber" />
            <StatCard label="Grand Total" value={fmt(detail.grand_total)}  color="accent" />
          </div>
          <Table cols={[
            {key:'description',  label:'Description'},
            {key:'quantity',     label:'Qty',    render:v=>fmtNum(v)},
            {key:'unit_of_measure',label:'Unit'},
            {key:'unit_rate',    label:'Rate',   render:v=>fmt(v)},
            {key:'amount',       label:'Amount', render:v=><span style={{fontFamily:'var(--mono)'}}>{fmt(v)}</span>},
          ]} rows={detail.items || []} />
        </Modal>
      )}

      {modal && (
        <Modal title="New Purchase Order" onClose={() => setModal(false)} width={740}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <FormGrid>
              <Input label="PO Number"      required value={form.po_number||''} onChange={v=>setForm(f=>({...f,po_number:v}))} />
              <Input label="Supplier Name"  required value={form.supplier_name||''} onChange={v=>setForm(f=>({...f,supplier_name:v}))} />
              <Input label="Supplier Contact" value={form.supplier_contact||''} onChange={v=>setForm(f=>({...f,supplier_contact:v}))} />
              <Input label="Order Date"     type="date" value={form.order_date||''} onChange={v=>setForm(f=>({...f,order_date:v}))} />
              <Input label="Expected Delivery" type="date" value={form.expected_delivery_date||''} onChange={v=>setForm(f=>({...f,expected_delivery_date:v}))} />
              <Input label="Total Amount"   type="number" value={form.total_amount||''} onChange={v=>setForm(f=>({...f,total_amount:v}))} />
              <Input label="VAT Amount"     type="number" value={form.vat_amount||''} onChange={v=>setForm(f=>({...f,vat_amount:v}))} />
              <Input label="Grand Total"    type="number" value={form.grand_total||''} onChange={v=>setForm(f=>({...f,grand_total:v}))} />
            </FormGrid>
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text-muted)', marginBottom:8 }}>LINE ITEMS</div>
              {form.items.map((item, i) => (
                <div key={i} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', gap:8, marginBottom:8, padding:10, background:'var(--bg-elevated)', borderRadius:8 }}>
                  <Input placeholder="Description" value={item.description||''} onChange={v=>setItem(i,'description',v)} />
                  <Input placeholder="Qty" type="number" value={item.quantity||''} onChange={v=>setItem(i,'quantity',v)} />
                  <Input placeholder="Unit" value={item.unit_of_measure||''} onChange={v=>setItem(i,'unit_of_measure',v)} />
                  <Input placeholder="Rate" type="number" value={item.unit_rate||''} onChange={v=>setItem(i,'unit_rate',v)} />
                </div>
              ))}
              <Btn variant="ghost" size="sm" onClick={() => setForm(f=>({...f,items:[...f.items,{}]}))}>Add Item</Btn>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} loading={saving}>Create PO</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── GRN ─────────────────────────────────────
function GRNTab({ projectId, toast }) {
  const [rows, setRows]       = useState([]);
  const [pos, setPOs]         = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [form, setForm]       = useState({ items: [{}] });
  const [saving, setSaving]   = useState(false);

  const load = async () => {
    setLoading(true);
    try { const [g, p] = await Promise.all([api.listGRNs(projectId), api.listPOs(projectId)]); setRows(Array.isArray(g)?g:[]); setPOs(Array.isArray(p)?p:[]); }
    catch {} setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [projectId]);

  const setItem = (i, k, v) => setForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));

  return (
    <>
      <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginBottom:14 }}>
        <Btn variant="ghost" onClick={() => exportCSV(rows, 'grn')}>⬇ CSV</Btn>
        <Btn onClick={() => { setForm({ items:[{}], received_date: new Date().toISOString().slice(0,10) }); setModal(true); }}>+ New GRN</Btn>
      </div>
      <Card>
        <Table loading={loading} cols={[
          { key:'grn_number',          label:'GRN #',    render:v=><span style={{fontFamily:'var(--mono)',color:'var(--green)'}}>{v}</span> },
          { key:'purchase_id',         label:'PO',       render:v=>{const p=pos.find(x=>x.purchase_id===v);return p?<span style={{fontFamily:'var(--mono)',fontSize:11}}>{p.po_number}</span>:'—';} },
          { key:'received_date',       label:'Date',     render:v=>fmtDate(v) },
          { key:'status',              label:'Status',   render:v=><Badge color={statusColor(v)}>{v}</Badge> },
          { key:'total_received_value',label:'Value',    render:v=><span style={{fontFamily:'var(--mono)'}}>{fmt(v)}</span> },
          { key:'grf_id',              label:'',         render:(id,row)=>row.status==='pending'&&(
            <Btn variant="success" size="sm" onClick={async()=>{try{await api.confirmGRN(id);toast.show('Confirmed');load();}catch(e){toast.show(e.message,'error');}}}>Confirm</Btn>
          )},
        ]} rows={rows} />
      </Card>

      {modal && (
        <Modal title="New GRN" onClose={() => setModal(false)} width={700}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <FormGrid>
              <Input label="GRN Number" required value={form.grn_number||''} onChange={v=>setForm(f=>({...f,grn_number:v}))} />
              <Select label="Purchase Order" required value={form.purchase_id||''} onChange={v=>setForm(f=>({...f,purchase_id:v}))}
                options={[{value:'',label:'— Select PO —'},...pos.map(p=>({value:p.purchase_id,label:p.po_number}))]} />
              <Input label="Received Date" type="date" required value={form.received_date||''} onChange={v=>setForm(f=>({...f,received_date:v}))} />
              <Input label="Delivery Note Ref" value={form.delivery_note_ref||''} onChange={v=>setForm(f=>({...f,delivery_note_ref:v}))} />
            </FormGrid>
            <Textarea label="Remarks" value={form.remarks||''} onChange={v=>setForm(f=>({...f,remarks:v}))} rows={2} />
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text-muted)', marginBottom:8 }}>ITEMS RECEIVED</div>
              {form.items.map((item, i) => (
                <div key={i} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:8, marginBottom:8, padding:10, background:'var(--bg-elevated)', borderRadius:8 }}>
                  <Input placeholder="Purchase Item ID" value={item.purchase_item_id||''} onChange={v=>setItem(i,'purchase_item_id',v)} />
                  <Input placeholder="Qty Received" type="number" value={item.quantity_received||''} onChange={v=>setItem(i,'quantity_received',v)} />
                  <Input placeholder="Unit Rate" type="number" value={item.unit_rate||''} onChange={v=>setItem(i,'unit_rate',v)} />
                </div>
              ))}
              <Btn variant="ghost" size="sm" onClick={() => setForm(f=>({...f,items:[...f.items,{}]}))}>Add Item</Btn>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn loading={saving} onClick={async()=>{setSaving(true);try{await api.createGRN({...form,project_id:projectId});toast.show('GRN created');setModal(false);load();}catch(e){toast.show(e.message,'error');}setSaving(false);}}>Create GRN</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── MAIN COMPONENT ──────────────────────────
function ProcurementDashboard({ projectId }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    prCount: 0,
    poCount: 0,
    grnCount: 0
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    const load = async () => {
      setLoading(true);
      try {
        const [pr, po, grn] = await Promise.all([
          api.listPRs(projectId).catch(() => []),
          api.listPOs(projectId).catch(() => []),
          api.listGRNs(projectId).catch(() => [])
        ]);
        setStats({
          prCount: pr.length,
          poCount: po.length,
          grnCount: grn.length
        });
      } catch (e) {}
      setLoading(false);
    };
    load();
  }, [projectId]);

  return (
    <>
      <SectionHeader title="Procurement Management" subtitle="Overview" />
      
      {loading ? (
        <RNLLoader title="Loading Procurement..." subtitle="Please wait" />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
          <StatCard label="Purchase Requests" value={stats.prCount} color="cyan" icon="✉️" />
          <StatCard label="Purchase Orders" value={stats.poCount} color="amber" icon="📋" />
          <StatCard label="Goods Received" value={stats.grnCount} color="green" icon="🚚" />
        </div>
      )}

      {/* DIVIDER */}
      <hr style={{ border: 'none', borderTop: '3px solid #94a3b8', margin: '0 0 32px 0' }} />

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Card
          className="toggle-on-hover"
          onClick={() => navigate(`/projects/${projectId}/procurement/pr`)}
          style={{ width: 220, padding: 24, cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, border: '1px solid black' }}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'black'}
        >
          <span style={{ fontSize: 32, padding: 12, background: 'var(--accent-dim)', borderRadius: 16 }}>✉️</span>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Purchase Requests</span>
        </Card>

        <Card
          className="toggle-on-hover"
          onClick={() => navigate(`/projects/${projectId}/procurement/po`)}
          style={{ width: 220, padding: 24, cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, border: '1px solid black' }}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'black'}
        >
          <span style={{ fontSize: 32, padding: 12, background: 'var(--accent-dim)', borderRadius: 16 }}>📋</span>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Purchase Orders</span>
        </Card>

        <Card
          className="toggle-on-hover"
          onClick={() => navigate(`/projects/${projectId}/procurement/grn`)}
          style={{ width: 220, padding: 24, cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, border: '1px solid black' }}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'black'}
        >
          <span style={{ fontSize: 32, padding: 12, background: 'var(--accent-dim)', borderRadius: 16 }}>🚚</span>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Goods Received</span>
        </Card>
      </div>
    </>
  );
}

export default function ProcurementPage({ toast }) {
  const { projectId, tab } = useParams();
  
  if (!projectId) return <NeedProject />;
  return (
    <div className="fade-in">
      {!tab && <ProcurementDashboard projectId={projectId} />}
      {tab && <SectionHeader title="Procurement" subtitle={tab === 'pr' ? 'Purchase Requests' : tab === 'po' ? 'Purchase Orders' : 'Goods Received'} />}
      {tab==='pr'  && <PRTab  projectId={projectId} toast={toast} />}
      {tab==='po'  && <POTab  projectId={projectId} toast={toast} />}
      {tab==='grn' && <GRNTab projectId={projectId} toast={toast} />}
    </div>
  );
}
